/**
 * @file src/sync/trackUserSettingsPersistence.js
 * @description Debounced persistence for private per-user sync/grid-marker settings.
 */
import { Constants } from '../config/Constants.js';
import { saveTrackUserSettings } from '../api/trackUserSettingsService.js';
import { configurationEpoch, isConfigurationEpochCurrent } from '../config/configurationPack.js';
import { isValidMeterId } from './meter.js';
import { foldPersonalSyncOverResolved, mayCommitWholesaleSync } from './personalTrackSync.js';

const SAVE_DELAY_MS = 280;

// One debounce queue PER trackId, so a fast track switch (edit A's BPM → switch to B →
// edit B within the debounce window) can't clobber A's pending save, and each track dedups against
// its own last save. Single-orbiter (one track at a time) behaves exactly as the prior single queue.
//
// A queue also belongs to ONE PERSON, and says so. Everything in it — the pending payload, and the
// fingerprint of what was last sent — is an account's, while the identity the PUT is actually made
// as is resolved when the debounce fires, a third of a second later. Between those two moments a
// shared browser can sign somebody else in, or an embed's host can swap tokens.
const queues = new Map(); // trackId -> { epoch, timer, payload, resolvers, inFlightSave, lastSavedFingerprint }

/**
 * Retire a queue whose person has left: nothing pending is sent as somebody else, and nothing it
 * remembers can act on the arriving person's saves.
 *
 * The payload goes because sending it would put one account's wholesale tempo, grid, meter, warp
 * and loop into another account's row — which is a destroyed set of real settings, not a lost edit,
 * since the backend replaces a track's stored `sync` rather than merging into it. The edit itself
 * is lost, and that is the only safe outcome available: it was made by somebody who is no longer
 * here, and there is nobody to send it as.
 *
 * The FINGERPRINT goes with it. Kept, it would dedup the arriving person's first save against the
 * departed one's — B setting the tempo A had just set would be silently not sent at all.
 */
function retireQueue(trackId, queue) {
  if (queue.timer) {
    clearTimeout(queue.timer);
    queue.timer = null;
  }
  const resolvers = queue.resolvers.slice();
  queue.payload = null;
  queue.resolvers = [];
  queues.delete(trackId);
  resolvers.forEach(({ resolve }) => resolve(null));
}

function getQueue(trackId) {
  const existing = queues.get(trackId);
  // Queues are per-track and flush lazily, so a change of identity can leave several of them
  // holding the previous person's work. Each is retired the moment it is next touched — here, or by
  // its own timer in `flushPendingSave` — which needs no identity hook of its own and cannot miss
  // one, unlike a sweep that has to be called from the right place at the right time.
  if (existing && !isConfigurationEpochCurrent(existing.epoch)) retireQueue(trackId, existing);

  let queue = queues.get(trackId);
  if (!queue) {
    queue = {
      epoch: configurationEpoch(),
      timer: null,
      payload: null,
      resolvers: [],
      inFlightSave: Promise.resolve(),
      lastSavedFingerprint: null,
    };
    queues.set(trackId, queue);
  }
  return queue;
}

function toPositiveNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function toNonNegativeNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
}

function buildGridMarkerRecord(sourceTimeSec) {
  const normalizedSourceTimeSec = toNonNegativeNumber(sourceTimeSec);
  if (normalizedSourceTimeSec === null) {
    return null;
  }

  return {
    id: 'primary',
    beat: 1,
    kind: 'downbeat',
    label: 'Grid marker',
    sourceTimeSec: normalizedSourceTimeSec,
  };
}

function buildSyncPayload({
  trackBpm = null,
  gridMarkerTimeSec = null,
  meter = null,
  warpEnabled = null,
  loopEnabled = null,
  loopStartSec = null,
  loopEndSec = null,
} = {}) {
  const normalizedTrackBpm = toPositiveNumber(trackBpm);
  const gridMarker = buildGridMarkerRecord(gridMarkerTimeSec);
  const sync = {};

  if (normalizedTrackBpm !== null) {
    sync.trackBpm = normalizedTrackBpm;
  }
  if (gridMarker) {
    sync.gridMarkers = [gridMarker];
  }
  // Warp is the only BOOLEAN in this payload, so it is the only field where the "omit when absent"
  // rule above needs a type test rather than a truthiness test. `if (warpEnabled)` here would write
  // warp-on and silently drop every warp-off — the exact preference the user took an action to set.
  // The caller passes null (not false) to mean "no opinion, leave whatever is stored alone".
  if (typeof warpEnabled === 'boolean') {
    sync.warpEnabled = warpEnabled;
  }
  // Loop on/off: same boolean rule as warp — a type test, so an explicit off is written.
  if (typeof loopEnabled === 'boolean') {
    sync.loopEnabled = loopEnabled;
  }
  // The loop REGION, written as a pair or not at all. Half a region is not half a loop, it is a
  // broken one, and the backend rejects it anyway — agreeing here keeps the two validations honest.
  // Omitting the pair is also how a CLEARED loop erases the stored one, since the backend replaces
  // a track's settings wholesale.
  const normalizedLoopStartSec = toNonNegativeNumber(loopStartSec);
  const normalizedLoopEndSec = toNonNegativeNumber(loopEndSec);
  if (
    normalizedLoopStartSec !== null
    && normalizedLoopEndSec !== null
    && normalizedLoopEndSec > normalizedLoopStartSec
  ) {
    sync.loopStartSec = normalizedLoopStartSec;
    sync.loopEndSec = normalizedLoopEndSec;
  }
  // The per-track meter (time signature), saved alongside tempo. Only written when a valid
  // meter id is supplied — mirrors trackBpm's "omit when absent" so a partial save never clobbers it.
  if (isValidMeterId(meter)) {
    sync.meter = meter;
  }

  return Object.keys(sync).length ? { sync } : null;
}

function getCurrentTrackId(trackId = null) {
  // The caller passes the active voice's trackId explicitly (no Constants.TRACK_ID global).
  if (typeof trackId === 'string' && trackId.trim().length) {
    return trackId.trim();
  }
  return null;
}

function updateCurrentConfigCache(trackId, payload) {
  if (!payload?.sync) return;
  // EVERY config holding this track, not the first one the snapshot map happens to yield.
  //
  // A person's own layer belongs to them and the track, not to one voice's arrangement of it — so
  // two stages showing the same track hold two cached copies of one fact, and both are superseded
  // by this save. Patching only the first mattered little while this merely assigned values over;
  // it matters now that the fold can DELETE a field, because the voice that actually saved could be
  // left holding a setting the save removed, and revert to it on the next re-seed.
  for (const currentConfig of Constants?.getConfigsByTrackId?.(trackId) || []) {
    if (currentConfig?.track?.trackId !== trackId) continue;
    patchCachedConfig(currentConfig, trackId, payload);
  }
}

function patchCachedConfig(currentConfig, trackId, payload) {
  // The server REPLACED this track's stored sync with the payload rather than merging into it, so
  // the cached copy of the person's own layer IS the payload. Laying the payload over what was
  // cached before is how a field the save deleted survived locally — a cleared loop region is
  // expressed by omitting the pair, and the merge kept handing back the region that had just gone.
  // The other sections (ui, playback, custom) are not part of this payload and are left alone.
  currentConfig.trackUserSettings = {
    ...(currentConfig.trackUserSettings || {}),
    trackId,
    sync: { ...payload.sync },
  };

  // The cached config also carries the SERVER-resolved block, and the resolvers read that FIRST —
  // it is the already-decided answer. Patching only the per-user copy above would leave the stale
  // resolved value shadowing what was just saved, so a re-seed within this session would revert the
  // toggle the user just made. It gets the SAME fold the boot path applies, so the two cannot
  // disagree about key names, source markers or what a missing loop region means — patched in place
  // because other holders are already pointing at this object.
  const resolvedSync = currentConfig?.track?.metadata?.effectiveSettings?.sync;
  if (resolvedSync && typeof resolvedSync === 'object') {
    const folded = foldPersonalSyncOverResolved(resolvedSync, payload.sync);
    for (const key of Object.keys(resolvedSync)) {
      if (!(key in folded)) delete resolvedSync[key];
    }
    Object.assign(resolvedSync, folded);
  }
}

async function flushPendingSave(trackId) {
  const queue = queues.get(trackId);
  if (!queue) return null;

  // The wholesale guard was answered when this was QUEUED; the identity the request is made as is
  // resolved down in the service, now. A tempo edit that passed the guard as one person, followed
  // by a sign-in before the timer fired, PUT that person's whole sync block into the arriving
  // account. Re-checked here, at the last moment before it goes, because that is the only place
  // both facts exist at once.
  if (!isConfigurationEpochCurrent(queue.epoch)) {
    console.warn(
      '[TrackSettings] Not saving: the person who made this edit left before it was sent, and the '
      + 'save replaces a track\'s whole stored block — sending it would write their settings into '
      + 'somebody else\'s account.',
      { trackId },
    );
    retireQueue(trackId, queue);
    return null;
  }

  const payload = queue.payload;
  const resolvers = queue.resolvers.slice();
  queue.payload = null;
  queue.resolvers = [];

  if (!payload) {
    resolvers.forEach(({ resolve }) => resolve(null));
    return null;
  }

  const fingerprint = JSON.stringify({ trackId, payload });
  if (fingerprint === queue.lastSavedFingerprint) {
    resolvers.forEach(({ resolve }) => resolve(null));
    return null;
  }

  try {
    const saved = await saveTrackUserSettings(trackId, payload, { promptOnAuthError: true });
    queue.lastSavedFingerprint = fingerprint;
    updateCurrentConfigCache(trackId, payload);
    resolvers.forEach(({ resolve }) => resolve(saved));
    return saved;
  } catch (error) {
    resolvers.forEach(({ reject }) => reject(error));
    throw error;
  }
}

/**
 * Queues a save for the current track's sync/grid-marker settings.
 *
 * The payload is intentionally limited to the current sync subset so this
 * path can ship now without coupling to MIDI migration.
 *
 * @param {{
 *   trackId?: string | null,
 *   trackBpm?: number | null,
 *   gridMarkerTimeSec?: number | null,
 *   warpEnabled?: boolean | null,
 *   loopEnabled?: boolean | null,
 *   loopStartSec?: number | null,
 *   loopEndSec?: number | null,
 *   trackUserSettingsOutcome?: string | null,
 *   immediate?: boolean
 * }} [options] `warpEnabled` and `loopEnabled` take a real boolean to save one, or null for "no
 * opinion". The loop region is written only as a complete pair; passing one edge writes neither.
 * `trackUserSettingsOutcome` is the provenance of the config being edited, from the caller that
 * knows WHICH voice that is — see the guard below.
 * @returns {Promise<object|null>}
 */
export function queueTrackSyncSettingsSave({
  trackId = null,
  trackBpm = null,
  gridMarkerTimeSec = null,
  meter = null,
  warpEnabled = null,
  loopEnabled = null,
  loopStartSec = null,
  loopEndSec = null,
  trackUserSettingsOutcome = null,
  immediate = false,
} = {}) {
  const resolvedTrackId = getCurrentTrackId(trackId);
  const payload = buildSyncPayload({
    trackBpm, gridMarkerTimeSec, meter, warpEnabled, loopEnabled, loopStartSec, loopEndSec,
  });

  if (!resolvedTrackId || !payload) {
    return Promise.resolve(null);
  }

  // The backend replaces a track's stored `sync` WHOLESALE, which is why every field is re-sent
  // above. That only works when the values being re-sent are the person's — and after a settings
  // read that could not be answered they are not: they are defaults and auto-detected values
  // standing in for an answer nobody could get. Writing them would delete real saved settings on a
  // session that merely had a bad moment on the network.
  //
  // Refused rather than partially written, because there is no partial write to make: a payload
  // this path can send always replaces everything.
  //
  // The caller states the provenance when it knows it, because it is the one that knows which VOICE
  // is being edited — two voices can hold the same track and read the settings at different moments,
  // one answered and one not. Left unstated, the guard has to fall back to "every config this track
  // is held under agrees", which is safe but coarser.
  if (!mayCommitWholesaleSync(resolvedTrackId, trackUserSettingsOutcome)) {
    console.warn(
      '[TrackSettings] Not saving: this track\'s saved settings could not be read, so the values '
      + 'in memory are defaults rather than choices, and the save would replace the real ones.',
      { trackId: resolvedTrackId },
    );
    return Promise.resolve(null);
  }

  const queue = getQueue(resolvedTrackId);
  queue.payload = payload;

  if (queue.timer) {
    clearTimeout(queue.timer);
    queue.timer = null;
  }

  const runSave = () => {
    queue.inFlightSave = queue.inFlightSave
      .catch(() => null)
      .then(() => flushPendingSave(resolvedTrackId));
    return queue.inFlightSave;
  };

  if (immediate) {
    return runSave();
  }

  return new Promise((resolve, reject) => {
    queue.resolvers.push({ resolve, reject });
    queue.timer = globalThis.setTimeout(() => {
      queue.timer = null;
      runSave().catch(() => null);
    }, SAVE_DELAY_MS);
  });
}
